import { motion } from 'framer-motion';
import { Layout, useLang } from '@/components/Layout';
import { COMPANY_INFO, PRODUCTS, SUPPLIERS, STATS, SERVICES, FINANCIAL_DATA } from '@/lib/index';
import { IMAGES } from '@/assets/images';
import {
  ArrowRight, ArrowLeft, Award, CheckCircle2, Wheat,
  Wrench, Settings, Droplets, Fuel, Phone, Mail, MapPin,
  TrendingUp, Users, Building2, Calendar
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

/* ─── Animation variants ─── */
const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

/* ─── Product icon map ─── */
const iconMap: Record<string, React.ReactNode> = {
  tractor:   <Settings className="w-7 h-7" />,
  harvester: <Wheat className="w-7 h-7" />,
  farming:   <Wrench className="w-7 h-7" />,
  water:     <Droplets className="w-7 h-7" />,
  oils:      <Fuel className="w-7 h-7" />,
  service:   <Award className="w-7 h-7" />,
};

const statIcons: React.ReactNode[] = [
  <Calendar className="w-7 h-7" />,
  <Users className="w-7 h-7" />,
  <Building2 className="w-7 h-7" />,
  <Award className="w-7 h-7" />,
];

/* ─── Inner page component that uses useLang ─── */
function HomeContent() {
  const { lang } = useLang();
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
  const ArrowIcon = lang === 'ar' ? ArrowLeft : ArrowRight;

  return (
    <>
      {/* ════════════════════════════════════════════
          1. HERO
      ════════════════════════════════════════════ */}
      <section id="hero" className="relative min-h-screen flex items-center overflow-hidden">

        {/* Background image */}
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.HARVESTER_ACTION_3}
            alt="Combine harvester"
            className="w-full h-full object-cover object-center"
          />
          {/* Dark overlay */}
          <div className="absolute inset-0" style={{
            background: lang === 'ar'
              ? 'linear-gradient(to left, rgba(9,12,9,0.92) 0%, rgba(9,12,9,0.7) 55%, rgba(9,12,9,0.2) 100%)'
              : 'linear-gradient(to right, rgba(9,12,9,0.92) 0%, rgba(9,12,9,0.7) 55%, rgba(9,12,9,0.2) 100%)'
          }} />
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-6 py-24">
          <div className={`max-w-2xl ${lang === 'ar' ? 'mr-0 ml-auto text-right' : ''}`}>

            {/* Label */}
            <motion.div
              initial={{ opacity: 0, x: lang === 'ar' ? 20 : -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="section-label mb-6"
              style={{ color: '#73CB60', justifyContent: lang === 'ar' ? 'flex-end' : 'flex-start' }}
            >
              <span className="block w-7 h-0.5 flex-shrink-0" style={{ background: '#73CB60' }} />
              {lang === 'ar'
                ? 'استيراد الآلات الزراعية وقطع الغيار'
                : 'Agricultural Machinery & Spare Parts'}
            </motion.div>

            {/* Headline */}
            <motion.h1
              className="font-black text-white leading-tight mb-6"
              style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)' }}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
            >
              {lang === 'ar' ? (
                <>
                  شركة{' '}
                  <span style={{ color: '#73CB60' }}>القطوف الخضراء</span>
                  <br />لاستيراد الآلات الزراعية
                </>
              ) : (
                <>
                  <span style={{ color: '#73CB60' }}>Al-Qutoof Al-Qhadraa</span>
                  <br />for Agricultural Machinery
                </>
              )}
            </motion.h1>

            {/* Sub */}
            <motion.p
              className="text-lg text-white/70 mb-10 leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              {lang === 'ar'
                ? 'موزّعون معتمدون لكبرى ماركات الآلات الزراعية العالمية. نخدم المزارعين والقطاع الحكومي في ليبيا منذ الثمانينيات.'
                : 'Authorized distributors of world-leading agricultural machinery brands. Serving farmers and government projects across Libya since the 1980s.'}
            </motion.p>

            {/* CTAs */}
            <motion.div
              className={`flex flex-wrap gap-4 ${lang === 'ar' ? 'justify-end' : ''}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              <a href="#products" onClick={e => { e.preventDefault(); document.querySelector('#products')?.scrollIntoView({ behavior: 'smooth' }); }} className="btn-orange flex items-center gap-2">
                {lang === 'ar' ? 'استكشف المنتجات' : 'Explore Products'}
                <ArrowIcon className="w-4 h-4" />
              </a>
              <a href="#contact" onClick={e => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }} className="btn-outline-green">
                {lang === 'ar' ? 'تواصل معنا' : 'Get In Touch'}
              </a>
            </motion.div>
          </div>
        </div>

        {/* Bottom orange bar */}
        <div className="absolute bottom-0 left-0 right-0 h-1" style={{ background: '#FF6000' }} />

        {/* Logo watermark */}
        <div className={`absolute bottom-8 ${lang === 'ar' ? 'left-8' : 'right-8'} z-10`}>
          <img src="/images/Screenshot9609.png" alt="Logo" className="w-16 h-16 object-contain opacity-50 brightness-0 invert" />
        </div>
      </section>

      {/* ════════════════════════════════════════════
          2. STATS BANNER
      ════════════════════════════════════════════ */}
      <section style={{ background: '#F9F4F0' }} className="py-14 border-b border-border">
        <div className="container mx-auto px-6">
          <motion.div
            className="grid grid-cols-2 md:grid-cols-4 gap-6"
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {STATS.map((stat, i) => (
              <motion.div key={stat.id} variants={fadeUp} className="stat-card">
                <div className="icon-circle mx-auto mb-4">
                  <span style={{ color: '#73CB60' }}>{statIcons[i]}</span>
                </div>
                <div className="text-4xl font-black mb-2" style={{ color: '#090C09' }}>{stat.value}</div>
                <div className="text-sm font-semibold text-muted-foreground">
                  {lang === 'ar' ? stat.labelAr : stat.labelEn}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          3. ABOUT — split layout like Weblium
      ════════════════════════════════════════════ */}
      <section id="about" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className={`grid md:grid-cols-2 gap-16 items-center ${lang === 'ar' ? '' : ''}`}>

            {/* Image col */}
            <motion.div
              className={`relative ${lang === 'ar' ? 'order-2' : 'order-1'}`}
              initial={{ opacity: 0, x: lang === 'ar' ? 40 : -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              {/* Main image */}
              <div className="relative overflow-hidden rounded-lg aspect-[4/3]">
                <img src={IMAGES.HARVESTER_ACTION_8} alt="About us" className="w-full h-full object-cover" />
                {/* Green badge */}
                <div className="absolute bottom-5 right-5 bg-white rounded-lg p-4 shadow-lg flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#73CB60' }}>
                    <Calendar className="w-5 h-5" style={{ color: '#090C09' }} />
                  </div>
                  <div>
                    <p className="text-xl font-black" style={{ color: '#090C09' }}>40+</p>
                    <p className="text-xs text-muted-foreground">
                      {lang === 'ar' ? 'سنة خبرة' : 'Years of Experience'}
                    </p>
                  </div>
                </div>
              </div>
              {/* Accent square */}
              <div className="absolute -bottom-5 -left-5 w-28 h-28 rounded-lg -z-10 opacity-20" style={{ background: '#73CB60' }} />
            </motion.div>

            {/* Text col */}
            <motion.div
              className={`${lang === 'ar' ? 'order-1 text-right' : 'order-2'}`}
              initial={{ opacity: 0, x: lang === 'ar' ? -40 : 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <div className={`section-label ${lang === 'ar' ? 'flex-row-reverse justify-end' : ''}`}>
                {lang === 'ar' ? 'عن الشركة' : 'About Us'}
              </div>
              <h2 className="text-4xl md:text-5xl font-black mb-6 leading-tight" style={{ color: '#2A292F' }}>
                {lang === 'ar' ? (
                  <>منذ الثمانينيات<br /><span style={{ color: '#73CB60' }}>نخدم الزراعة الليبية</span></>
                ) : (
                  <>Since the 1980s<br /><span style={{ color: '#73CB60' }}>Serving Libyan Agriculture</span></>
                )}
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                {lang === 'ar'
                  ? 'بدأنا كورشة إصلاح صغيرة للآلات الزراعية في المرج، وتطوّرنا لنصبح شركة استيراد متكاملة مسجّلة رسمياً في 2014، بفريق من 12 متخصصاً يخدم المزارعين والمشاريع الحكومية في كافة أنحاء ليبيا.'
                  : 'We began as a small repair workshop for agricultural machinery in Al Marj, growing into a full-fledged import company formally registered in 2014. Our team of 12 specialists serves farmers and government agricultural projects across Libya.'}
              </p>

              {/* Two highlights */}
              <div className="grid grid-cols-2 gap-5 mb-8">
                <div className="p-5 rounded-lg border border-border" style={{ background: '#F9F4F0' }}>
                  <p className="text-2xl font-black mb-1" style={{ color: '#090C09' }}>2014</p>
                  <p className="text-sm text-muted-foreground">
                    {lang === 'ar' ? 'سنة التأسيس الرسمي' : 'Year of formal registration'}
                  </p>
                </div>
                <div className="p-5 rounded-lg border border-border" style={{ background: '#F9F4F0' }}>
                  <p className="text-2xl font-black mb-1" style={{ color: '#090C09' }}>12</p>
                  <p className="text-sm text-muted-foreground">
                    {lang === 'ar' ? 'متخصص في الفريق' : 'Specialists in our team'}
                  </p>
                </div>
              </div>

              {/* Vision & Mission */}
              <div className={`space-y-3 ${lang === 'ar' ? 'text-right' : ''}`}>
                <div className={`flex items-start gap-3 ${lang === 'ar' ? 'flex-row-reverse' : ''}`}>
                  <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#73CB60' }} />
                  <div>
                    <p className="font-bold text-sm mb-0.5" style={{ color: '#2A292F' }}>
                      {lang === 'ar' ? 'رؤيتنا' : 'Our Vision'}
                    </p>
                    <p className="text-sm text-muted-foreground">{lang === 'ar' ? COMPANY_INFO.visionAr : COMPANY_INFO.visionEn}</p>
                  </div>
                </div>
                <div className={`flex items-start gap-3 ${lang === 'ar' ? 'flex-row-reverse' : ''}`}>
                  <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#73CB60' }} />
                  <div>
                    <p className="font-bold text-sm mb-0.5" style={{ color: '#2A292F' }}>
                      {lang === 'ar' ? 'مهمّتنا' : 'Our Mission'}
                    </p>
                    <p className="text-sm text-muted-foreground">{lang === 'ar' ? COMPANY_INFO.missionAr : COMPANY_INFO.missionEn}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          4. PRODUCTS — card grid like Weblium
      ════════════════════════════════════════════ */}
      <section id="products" style={{ background: '#F9F4F0' }} className="py-24">
        <div className="container mx-auto px-6">
          <motion.div
            className={`text-center mb-16 ${lang === 'ar' ? 'text-right' : ''}`}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <div className={`section-label ${lang === 'ar' ? 'flex-row-reverse justify-end' : 'justify-center'}`}>
              {lang === 'ar' ? 'ما نقدمه' : 'What We Offer'}
            </div>
            <h2 className={`text-4xl md:text-5xl font-black mb-4 ${lang === 'ar' ? '' : 'text-center'}`} style={{ color: '#2A292F' }}>
              {lang === 'ar' ? 'نطاق منتجاتنا' : 'Our Product Range'}
            </h2>
            <p className={`text-muted-foreground max-w-xl ${lang === 'ar' ? 'mr-0 ml-auto' : 'mx-auto'}`}>
              {lang === 'ar'
                ? 'نوفر مجموعة شاملة من الآلات الزراعية وقطع الغيار والمستلزمات من أفضل الموردين العالميين.'
                : 'We provide a comprehensive range of agricultural machinery, spare parts, and supplies from the world\'s best suppliers.'}
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {PRODUCTS.map((product, idx) => {
              const iconKeys = ['tractor', 'harvester', 'farming', 'water', 'oils', 'service'];
              const iconKey = iconKeys[idx] || 'service';
              return (
                <motion.div key={product.id} variants={fadeUp} className="product-card group">
                  {/* Top image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={[
                        IMAGES.TRACTOR_GREEN_3,
                        IMAGES.HARVESTER_ACTION_2,
                        IMAGES.TRACTOR_GREEN_6,
                        IMAGES.GRAIN_TEXTURE_4,
                        IMAGES.WORKSHOP_PARTS_3,
                        IMAGES.WORKSHOP_PARTS_6,
                      ][idx] || IMAGES.HARVESTER_ACTION_1}
                      alt={product.nameEn}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    {/* Number badge */}
                    <div className="absolute top-4 left-4 w-9 h-9 rounded-full flex items-center justify-center font-black text-sm" style={{ background: '#FF6000', color: '#fff' }}>
                      {idx + 1}
                    </div>
                  </div>

                  {/* Body */}
                  <div className={`p-6 ${lang === 'ar' ? 'text-right' : ''}`}>
                    <div className={`flex items-center gap-3 mb-4 ${lang === 'ar' ? 'flex-row-reverse' : ''}`}>
                      <div className="icon-circle product-card-icon flex-shrink-0">
                        {iconMap[iconKey]}
                      </div>
                      <div>
                        <h3 className="font-black text-lg leading-tight" style={{ color: '#2A292F' }}>
                          {lang === 'ar' ? product.nameAr : product.nameEn}
                        </h3>
                        <p className="text-xs text-muted-foreground font-mono">
                          {lang === 'ar' ? product.nameEn : product.nameAr}
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{product.description}</p>

                    {/* CTA */}
                    <div className={`mt-5 pt-5 border-t border-border flex ${lang === 'ar' ? 'justify-start' : 'justify-end'}`}>
                      <a
                        href="#contact"
                        onClick={e => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }}
                        className="flex items-center gap-1.5 text-sm font-bold transition-colors"
                        style={{ color: '#73CB60' }}
                      >
                        {lang === 'ar' ? 'استفسر الآن' : 'Enquire Now'}
                        <ArrowIcon className="w-4 h-4" />
                      </a>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          5. SERVICES — split with image
      ════════════════════════════════════════════ */}
      <section id="services" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16 items-center">

            {/* Services list */}
            <motion.div
              className={`${lang === 'ar' ? 'order-2 text-right' : 'order-1'}`}
              initial={{ opacity: 0, x: lang === 'ar' ? 40 : -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <div className={`section-label ${lang === 'ar' ? 'flex-row-reverse justify-end' : ''}`}>
                {lang === 'ar' ? 'خدماتنا' : 'Our Services'}
              </div>
              <h2 className="text-4xl md:text-5xl font-black mb-8 leading-tight" style={{ color: '#2A292F' }}>
                {lang === 'ar' ? (
                  <>دعم متكامل<br /><span style={{ color: '#73CB60' }}>في كل مرحلة</span></>
                ) : (
                  <>Complete Support<br /><span style={{ color: '#73CB60' }}>At Every Stage</span></>
                )}
              </h2>

              <div className="space-y-5">
                {SERVICES.map((service, i) => (
                  <motion.div
                    key={service.id}
                    className={`flex gap-5 p-5 rounded-lg border border-border bg-secondary/30 hover:border-primary transition-all ${lang === 'ar' ? 'flex-row-reverse text-right' : ''}`}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#090C09' }}>
                      <span style={{ color: '#73CB60' }}>
                        {i === 0 ? <Wrench className="w-5 h-5" /> : i === 1 ? <MapPin className="w-5 h-5" /> : <CheckCircle2 className="w-5 h-5" />}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-black text-base mb-1" style={{ color: '#2A292F' }}>
                        {lang === 'ar' ? service.titleAr : service.titleEn}
                      </h4>
                      <p className="text-sm text-muted-foreground">{service.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Image */}
            <motion.div
              className={`${lang === 'ar' ? 'order-1' : 'order-2'} relative`}
              initial={{ opacity: 0, x: lang === 'ar' ? -40 : 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <div className="relative overflow-hidden rounded-lg aspect-square">
                <img src={IMAGES.WORKSHOP_PARTS_4} alt="Services" className="w-full h-full object-cover" />
                {/* Overlay badge */}
                <div className="absolute bottom-0 left-0 right-0 p-6" style={{ background: 'linear-gradient(to top, rgba(9,12,9,0.9), transparent)' }}>
                  <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#73CB60' }}>
                    {lang === 'ar' ? 'عمليات ميدانية' : 'Field Operations'}
                  </p>
                  <p className="text-white font-black text-xl">
                    {lang === 'ar' ? 'الكفرة والسرير' : 'Alkufra & Al-Sarir'}
                  </p>
                </div>
              </div>
              {/* Orange accent */}
              <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-full -z-10" style={{ background: '#FF6000', opacity: 0.15 }} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          6. SUPPLIERS — cream background
      ════════════════════════════════════════════ */}
      <section id="suppliers" style={{ background: '#F9F4F0' }} className="py-24">
        <div className="container mx-auto px-6">
          <motion.div
            className={`text-center mb-14 ${lang === 'ar' ? 'text-right' : ''}`}
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <div className={`section-label ${lang === 'ar' ? 'flex-row-reverse justify-end' : 'justify-center'}`}>
              {lang === 'ar' ? 'شركاؤنا' : 'Our Partners'}
            </div>
            <h2 className={`text-4xl md:text-5xl font-black mb-4 ${lang === 'ar' ? '' : 'text-center'}`} style={{ color: '#2A292F' }}>
              {lang === 'ar' ? 'موزّعون معتمدون' : 'Authorized Distributors'}
            </h2>
            <p className={`text-muted-foreground max-w-xl ${lang === 'ar' ? 'mr-0 ml-auto' : 'mx-auto'}`}>
              {lang === 'ar'
                ? 'نمثّل أبرز العلامات التجارية الزراعية العالمية ونوزع منتجاتها في السوق الليبي.'
                : 'We represent and distribute products of the world\'s leading agricultural machinery brands in the Libyan market.'}
            </p>
          </motion.div>

          {/* Current distributors */}
          <div className="mb-12">
            <p className={`text-xs font-bold uppercase tracking-widest mb-6 ${lang === 'ar' ? 'text-right' : ''}`} style={{ color: '#73CB60' }}>
              {lang === 'ar' ? 'موزّعون حاليون' : 'Current Authorized Distributors'}
            </p>
            <div className="grid md:grid-cols-2 gap-6 max-w-2xl">
              {SUPPLIERS.filter(s => s.status === 'current').map(supplier => (
                <motion.div
                  key={supplier.id}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className={`supplier-badge relative overflow-hidden ${lang === 'ar' ? 'text-right' : ''}`}
                >
                  <div className="absolute top-0 left-0 w-1 h-full" style={{ background: '#73CB60' }} />
                  <Award className="w-10 h-10 mb-3" style={{ color: '#73CB60' }} />
                  <h4 className="text-2xl font-black" style={{ color: '#2A292F' }}>{supplier.nameEn}</h4>
                  <p className="text-sm text-muted-foreground">{supplier.nameAr}</p>
                  <span className="inline-block mt-2 px-3 py-1 rounded-full text-xs font-bold" style={{ background: '#F9F4F0', color: '#73CB60', border: '1px solid #73CB60' }}>
                    {supplier.country}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Former */}
          <div className="mb-12">
            <p className={`text-xs font-bold uppercase tracking-widest mb-6 ${lang === 'ar' ? 'text-right' : ''}`} style={{ color: '#9ca3af' }}>
              {lang === 'ar' ? 'موزّع سابق (2019–2024)' : 'Former Distributor (2019–2024)'}
            </p>
            <div className="grid md:grid-cols-2 gap-6 max-w-2xl">
              {SUPPLIERS.filter(s => s.status === 'former').map(supplier => (
                <div
                  key={supplier.id}
                  className={`supplier-badge opacity-60 ${lang === 'ar' ? 'text-right' : ''}`}
                >
                  <h4 className="text-xl font-black text-muted-foreground">{supplier.nameEn}</h4>
                  <p className="text-sm text-muted-foreground">{supplier.nameAr}</p>
                  {supplier.period && <p className="text-xs font-mono text-muted-foreground">{supplier.period}</p>}
                </div>
              ))}
            </div>
          </div>

          {/* Supplier Network */}
<div>
  <p className={`text-xs font-bold uppercase tracking-widest mb-6 ${lang === 'ar' ? 'text-right' : ''}`} style={{ color: '#73CB60' }}>
    {lang === 'ar' ? 'شبكة الموردين العالمية' : 'Global Supplier Network'}
  </p>
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
    {SUPPLIERS.filter(s => s.status === 'network').map(supplier => (
      <motion.div
        key={supplier.id}
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className={`supplier-badge flex flex-col items-center justify-center p-4 border rounded-lg ${lang === 'ar' ? 'text-right' : 'text-center'}`}
      >
        {/* عرض الشعار إذا وجد */}
        {supplier.logo && (
          <img 
            src={supplier.logo} 
            alt={supplier.nameEn} 
            className="h-12 w-auto object-contain mb-3" // تحكم في حجم الشعار هنا
          />
        )}
        
        <h4 className="text-lg font-black" style={{ color: '#2A292F' }}>
          {lang === 'ar' ? supplier.nameAr : supplier.nameEn}
        </h4>
        <p className="text-xs font-mono text-muted-foreground">{supplier.country}</p>
      </motion.div>
    ))}
  </div>
</div>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          7. MARKET & GROWTH
      ════════════════════════════════════════════ */}
      <section id="market" className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            className={`mb-16 ${lang === 'ar' ? 'text-right' : ''}`}
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <div className={`section-label ${lang === 'ar' ? 'flex-row-reverse justify-end' : ''}`}>
              {lang === 'ar' ? 'السوق الليبي' : 'The Libyan Market'}
            </div>
            <h2 className="text-4xl md:text-5xl font-black mb-4" style={{ color: '#2A292F' }}>
              {lang === 'ar' ? 'نموّ مستدام' : 'Sustainable Growth'}
            </h2>
            <p className="text-muted-foreground max-w-xl">
              {lang === 'ar'
                ? 'نرسّخ حضورنا في السوق الليبي بخدمة المزارعين الخاصين والقطاع الحكومي على حدٍّ سواء.'
                : 'We consolidate our position in the Libyan market serving both private farmers and the government sector.'}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {/* 25% private */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className={`p-8 rounded-xl border-2 border-border ${lang === 'ar' ? 'text-right' : 'text-center'}`}
            >
              <div className="text-6xl font-black mb-3" style={{ color: '#FF6000' }}>25%</div>
              <h4 className="font-black text-lg mb-2" style={{ color: '#2A292F' }}>
                {lang === 'ar' ? 'القطاع الخاص' : 'Private Sector'}
              </h4>
              <p className="text-sm text-muted-foreground">
                {lang === 'ar' ? 'مزارعون ومشاريع زراعية خاصة' : 'Private farmers & agricultural SMEs'}
              </p>
            </motion.div>

            {/* 75% government */}
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="p-8 rounded-xl text-white relative overflow-hidden text-center"
              style={{ background: '#090C09' }}
            >
              <div className="text-6xl font-black mb-3" style={{ color: '#73CB60' }}>75%</div>
              <h4 className="font-black text-lg mb-2">
                {lang === 'ar' ? 'القطاع الحكومي' : 'Government Sector'}
              </h4>
              <p className="text-sm text-white/60">
                {lang === 'ar' ? 'وزارة الزراعة الليبية' : 'Libyan Ministry of Agriculture'}
              </p>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-full opacity-10" style={{ background: '#73CB60' }} />
            </motion.div>

            {/* Field circles */}
            {/* <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className={`p-8 rounded-xl border-2 ${lang === 'ar' ? 'text-right' : 'text-center'}`}
              style={{ background: '#F9F4F0', borderColor: '#73CB60' }}
            >
              <div className="flex justify-center mb-4" style={{ justifyContent: lang === 'ar' ? 'flex-end' : 'center' }}>
                <Wheat className="w-12 h-12" style={{ color: '#73CB60' }} />
              </div>
              <div className="text-4xl font-black mb-1" style={{ color: '#2A292F' }}>3,000+</div>
              <h4 className="font-bold text-base mb-1" style={{ color: '#2A292F' }}>
                {lang === 'ar' ? 'دائرة حقلية' : 'Field Circles'}
              </h4>
              <p className="text-sm text-muted-foreground">125,000 {lang === 'ar' ? 'هكتار — قمح وشعير' : 'hectares — wheat & barley'}</p>
            </motion.div> */}
          </div>

          {/* Financial chart */}
          {/* <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="p-8 rounded-xl border border-border"
            style={{ background: '#F9F4F0' }}
          >
            <div className={`flex items-center justify-between mb-6 flex-wrap gap-4 ${lang === 'ar' ? 'flex-row-reverse' : ''}`}>
              <div className={lang === 'ar' ? 'text-right' : ''}>
                <div className="flex items-center gap-2 mb-1" style={{ justifyContent: lang === 'ar' ? 'flex-end' : 'flex-start' }}>
                  <TrendingUp className="w-5 h-5" style={{ color: '#73CB60' }} />
                  <h3 className="font-black text-xl" style={{ color: '#2A292F' }}>
                    {lang === 'ar' ? 'النمو المالي' : 'Financial Growth'}
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground font-mono">
                  {lang === 'ar' ? 'صافي الدخل (بالآلاف — يورو) · 2014 – 2024' : 'Net Income (Thousands EUR) · 2014 – 2024'}
                </p>
              </div>
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${lang === 'ar' ? '' : ''}`} style={{ background: '#090C09' }}>
                <span className="text-sm font-black" style={{ color: '#73CB60' }}>+103%</span>
                <span className="text-xs" style={{ color: 'rgba(249,244,240,0.6)' }}>
                  {lang === 'ar' ? 'نمو 2014-2024' : '2014–2024 Growth'}
                </span>
              </div>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={FINANCIAL_DATA} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e8e2dc" />
                  <XAxis dataKey="year" tick={{ fontSize: 12, fill: '#6b7280' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} />
                  <Tooltip
                    contentStyle={{
                      background: '#090C09', border: '1px solid #73CB60',
                      borderRadius: '6px', color: '#F9F4F0', fontSize: 13, fontWeight: 700
                    }}
                    formatter={(v: number) => [`€${v.toLocaleString()}k`, lang === 'ar' ? 'صافي الدخل' : 'Net Income']}
                  />
                  <Line type="monotone" dataKey="netIncome" stroke="#73CB60" strokeWidth={3}
                    dot={{ fill: '#fff', stroke: '#73CB60', r: 5, strokeWidth: 2 }}
                    activeDot={{ r: 8, fill: '#73CB60' }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </motion.div> */}
        </div>
      </section>

      {/* ════════════════════════════════════════════
          8. CTA BANNER — dark bg
      ════════════════════════════════════════════ */}
      <section className="relative py-20 overflow-hidden" style={{ background: '#090C09' }}>
        <div className="absolute inset-0 z-0">
          <img src={IMAGES.HERO_WHEAT_1} alt="Wheat field" className="w-full h-full object-cover opacity-20" />
        </div>
        <div className="relative z-10 container mx-auto px-6 text-center">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <p className="section-label mb-4" style={{ color: '#73CB60', justifyContent: 'center' }}>
              {lang === 'ar' ? 'استعد للموسم الزراعي' : 'Ready for the Farming Season'}
            </p>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight">
              {lang === 'ar'
                ? 'هل تحتاج إلى آلات أو قطع غيار؟'
                : 'Need Machinery or Spare Parts?'}
            </h2>
            <p className="text-white/60 max-w-xl mx-auto mb-10 text-lg">
              {lang === 'ar'
                ? 'تواصل مع فريقنا اليوم وسنوفر لك أفضل الحلول الزراعية بأسعار تنافسية.'
                : 'Contact our team today and we\'ll provide the best agricultural solutions at competitive prices.'}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="#contact" onClick={e => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }} className="btn-orange text-lg px-10 py-4">
                {lang === 'ar' ? 'تواصل معنا الآن' : 'Contact Us Now'}
              </a>
              <a href={`tel:${COMPANY_INFO.contact.phone}`} className="btn-outline-green text-lg px-10 py-4">
                <Phone className="w-5 h-5" />
                {COMPANY_INFO.contact.phone}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════════
          9. CONTACT
      ════════════════════════════════════════════ */}
      <section id="contact" style={{ background: '#F9F4F0' }} className="py-24">
        <div className="container mx-auto px-6">
          <motion.div
            className={`mb-16 ${lang === 'ar' ? 'text-right' : ''}`}
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <div className={`section-label ${lang === 'ar' ? 'flex-row-reverse justify-end' : ''}`}>
              {lang === 'ar' ? 'تواصل معنا' : 'Get In Touch'}
            </div>
            <h2 className="text-4xl md:text-5xl font-black" style={{ color: '#2A292F' }}>
              {lang === 'ar' ? 'نحن هنا لمساعدتك' : 'We\'re Here to Help'}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Contact cards */}
            {[
              {
                icon: <Phone className="w-7 h-7" />,
                titleEn: 'Phone', titleAr: 'الهاتف',
                info: COMPANY_INFO.contact.phone,
                href: `tel:${COMPANY_INFO.contact.phone}`
              },
              {
                icon: <Mail className="w-7 h-7" />,
                titleEn: 'Email', titleAr: 'البريد الإلكتروني',
                info: COMPANY_INFO.contact.email,
                href: `mailto:${COMPANY_INFO.contact.email}`
              },
              {
                icon: <MapPin className="w-7 h-7" />,
                titleEn: 'Offices', titleAr: 'المكاتب',
                info: lang === 'ar' ? 'طرابلس – النوفليين\nالمرج – الطريق الساحلي' : 'Tripoli – Al-Nofleen\nAl Marj – Coastal Road',
                href: '#'
              },
            ].map((item, i) => (
              <motion.a
                key={i}
                href={item.href}
                target={item.href.startsWith('#') ? undefined : '_blank'}
                rel="noopener noreferrer"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className={`stat-card flex flex-col items-center text-center cursor-pointer no-underline hover:border-primary transition-all ${lang === 'ar' ? 'text-right items-end' : ''}`}
                style={{ border: '1px solid #e8e2dc' }}
              >
                <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ background: '#090C09', color: '#73CB60' }}>
                  {item.icon}
                </div>
                <h4 className="font-black text-base mb-2" style={{ color: '#2A292F' }}>
                  {lang === 'ar' ? item.titleAr : item.titleEn}
                </h4>
                <p className="text-muted-foreground text-sm font-mono whitespace-pre-line">{item.info}</p>
              </motion.a>
            ))}
          </div>

          {/* Address cards */}
          <div className="grid md:grid-cols-2 gap-6 mt-10">
            {COMPANY_INFO.addresses.map((addr, i) => (
              <motion.div
                key={addr.id}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className={`bg-white rounded-xl border border-border p-6 flex gap-5 ${lang === 'ar' ? 'flex-row-reverse text-right' : ''}`}
              >
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#F9F4F0', color: '#73CB60' }}>
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <span className="inline-block px-2 py-0.5 rounded text-xs font-bold mb-2" style={{ background: addr.type === 'main' ? '#090C09' : '#F9F4F0', color: addr.type === 'main' ? '#73CB60' : '#6b7280' }}>
                    {addr.type === 'main'
                      ? (lang === 'ar' ? 'المقر الرئيسي' : 'Head Office')
                      : (lang === 'ar' ? 'مكتب الإدارة' : 'Administration Office')
                    }
                  </span>
                  <h4 className="font-black text-xl mb-1" style={{ color: '#2A292F' }}>
                    {lang === 'ar' ? addr.cityAr : addr.cityEn}
                  </h4>
                  <p className="text-muted-foreground text-sm">
                    {lang === 'ar' ? addr.locationAr : addr.locationEn}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

/* ─── Default export wraps with Layout ─── */
export default function Home() {
  return (
    <Layout>
      <HomeContent />
    </Layout>
  );
}
